import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
} from 'react';

type DrawerMenuState = {
  isOpen: boolean;
};

type DrawerMenuAction =
  | { type: 'SHOW_DRAWER_MENU' }
  | { type: 'HIDE_DRAWER_MENU' };

type DrawerMenuContextType = {
  drawerMenu: DrawerMenuState;
  showDrawerMenu: () => void;
  hideDrawerMenu: () => void;
};

const initialState: DrawerMenuState = {
  isOpen: false,
};

const drawerMenuReducer = (
  state: DrawerMenuState,
  action: DrawerMenuAction
): DrawerMenuState => {
  switch (action.type) {
    case 'SHOW_DRAWER_MENU':
      return {
        ...state,
        isOpen: true,
      };
    case 'HIDE_DRAWER_MENU':
      return {
        ...state,
        isOpen: false,
      };
    default:
      return state;
  }
};

const DrawerMenuContext = createContext<DrawerMenuContextType | undefined>(
  undefined
);

export const DrawerMenuProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [drawerMenu, dispatch] = useReducer(drawerMenuReducer, initialState);

  const showDrawerMenu = useCallback(() => {
    dispatch({ type: 'SHOW_DRAWER_MENU' });
  }, []);

  const hideDrawerMenu = useCallback(() => {
    dispatch({ type: 'HIDE_DRAWER_MENU' });
  }, []);

  return (
    <DrawerMenuContext.Provider
      value={{ drawerMenu, showDrawerMenu, hideDrawerMenu }}
    >
      {children}
    </DrawerMenuContext.Provider>
  );
};

export const useDrawerMenu = () => {
  const context = useContext(DrawerMenuContext);
  if (context === undefined) {
    throw new Error('useDrawerMenu must be used within a DrawerMenuProvider');
  }
  return context;
};
